import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, User } from "lucide-react";

import { LogoutButton } from "features/auth/components/LogoutButton";
import { pathGenerators } from "utils/routes";

import styles from "./ProfileDropdown.module.css";

export function ProfileDropdown({
  staffMember,
  nameState,
  signedInEmail,
  userState,
  userInitials,
  isLoaded,
}) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef(null);

  const handleToggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        handleClose();
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [isOpen, handleClose]);

  const handleProfileClick = useCallback(() => {
    navigate(pathGenerators.settings.profile());
    handleClose();
  }, [navigate, handleClose]);

  return (
    <>
      <div
        ref={containerRef}
        className={styles.profileToggle}
        role="navigation"
        aria-label="User menu"
      >
        <button
          type="button"
          className={styles.profileTrigger}
          onClick={handleToggle}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          <span className={styles.triggerIdentity}>
            <span className={styles.triggerName}>{nameState}</span>
          </span>
          {isLoaded && (
            <div className={styles.logiconbox}>
              {staffMember?.profilePic ? (
                <img
                  src={staffMember.profilePic}
                  alt=""
                  className={styles.profileImage}
                />
              ) : (
                <span className={styles.userInitials}>{userInitials}</span>
              )}
            </div>
          )}
        </button>
        {isOpen && (
          <ul className={styles.dropdownMenu} role="menu">
            <li className={`${styles.profileLink} ${styles.profileLinkHeader}`}>
              <div className={styles.profileLinkDetails}>
                {userState && (
                  <>
                    <h6>{nameState}</h6>
                    <p>{signedInEmail || userState.email}</p>
                  </>
                )}
              </div>
            </li>
            <li>
              <button
                type="button"
                className={styles.profileLink}
                onClick={handleProfileClick}
              >
                <div className={styles.dropIcon}>
                  <User size={18} strokeWidth={2} aria-hidden />
                </div>
                <div className={styles.profileLinkDetails}>
                  <h6>Profile</h6>
                </div>
              </button>
            </li>
            <li>
              <LogoutButton className={styles.profileLink}>
                <div className={styles.dropIcon}>
                  <LogOut size={18} strokeWidth={2} aria-hidden />
                </div>
                <div className={styles.profileLinkDetails}>
                  <h6>Logout</h6>
                </div>
              </LogoutButton>
            </li>
          </ul>
        )}
      </div>
    </>
  );
}
